import * as React from 'react'
import PropTypes from 'prop-types'
import InputGroup from 'react-bootstrap/InputGroup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import FormControl from 'react-bootstrap/FormControl'
import { useDebounce } from 'api/hooks/use-debounce'

/**
 * The caller's filter state. Only `search` and `page` are this component's
 * business; the index signature carries everything else through untouched, which
 * is what the setParams spread below relies on.
 */
type SearchParams = {
  search?: string,
  page?: number,
  [key: string]: unknown
}

type SearchProps = {
  params: SearchParams,
  setParams: Function,
  placeholder?: string,
  disabled?: boolean
}

function Search({ params, setParams, placeholder = 'Search Name', disabled = false }: SearchProps) {
  const [search, setSearch] = React.useState(params?.search || '')
  const debouncedSearch = useDebounce(search, 300)

  // Sync internal state when params.search changes externally (e.g., from AI search pre-fill)
  React.useEffect(() => {
    if (params?.search !== undefined && params.search !== search) {
      setSearch(params.search)
    }
  }, [params?.search]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!disabled) {
      setParams((prev: SearchParams) => ({ ...prev, search: debouncedSearch, page: 1 }))
    }
  }, [debouncedSearch, setParams, disabled])

  return (
    <InputGroup>
      <InputGroup.Text>
        <FontAwesomeIcon icon={faSearch} />
      </InputGroup.Text>
      <FormControl
        placeholder={placeholder}
        data-testid="search-input"
        value={search}
        onChange={(e) => !disabled && setSearch(e.target.value)}
        disabled={disabled}
      />
    </InputGroup>
  )
}

Search.propTypes = {
  setParams: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired
}

export default Search
